import React, { useEffect, useRef, useState } from 'react';
import './CorrectorNotes.scss';
import { cloneDeep } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, FormikHelpers as FormikActions } from 'formik';

import {
  get_bozza, patch_bozza, delete_bozza,
  post_new_problema,
  lint_text,
} from '../api';
import {
  BozzaProblemaData, Soluzione, CategoriaEvento, Problema, Linter
} from '../api/types';
import {
  FormGroupBootstrap, LoadingCircle, TextHelper, AnteprimaLaTeX,
  ConfirmedButton, MultipleSoluzioniForm, AllegatoMap, ErrorBlockGeneric,
  Anteprima, CategoriaEventoSelector, CompetenzaSelector,
  RisposteMultipleForm, CategoriaSelector, MinimalLatexEditor,
} from '../components'


import { APIError, handle_drf_form_error } from 'django-rest-react';
import { sentry_log, deserialize_versioned } from '../utils';
import store from '../store';
import { add_toast_action } from '../actions';
import { FONTE } from '../globals'
import { useAllegato, useCategoria, useCategoriaEvento, useEvento } from '../reducers';
import { PROBLEMA_TIPOLOGIA_RISPOSTA } from '../globals/auto-enums';


interface FormValues {
  titolo_meme: string,
  titolo: string,
  tipologia_risposta: Problema["tipologia_risposta"],
  allegati: number[],
  categoria: number[],
  eventi_proposto: number[],
  testo: string,
  soluzioni: Soluzione[],
  soluzione_spiegata: string,
  commenti_correttori: string,
  problema: {
    soluzioni: Soluzione[]
  }
}

enum Bottone {
  Save,
  Pubblica,
}

type Linted = {
  [key: string]: Linter[]
}

type Bozza = Awaited<ReturnType<typeof get_bozza>>

const ModificaBozza = (props: { pk: number }) => {

  const categorieEvento = useCategoriaEvento(props);
  useEvento(props);
  useCategoria(props);
  const allegati = useAllegato(props);

  const [old, setOld] = useState<Bozza | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lints, setLints] = useState({
    ["testo"]: [],
    ["soluzione_spiegata"]: [],
    ["commenti"]: [],
  } as Linted);

  const lastLint = useRef(new Date());
  const textarea = useRef(null);
  const subButton = useRef(Bottone.Save);
  const navigate = useNavigate();

  useEffect(() => {
    get_bozza(props.pk).then(setOld);
  }, [props.pk])


  const handleUpdate = async (values: FormValues, actions: FormikActions<FormValues>) => {
    return patch_bozza(props.pk, values as unknown as BozzaProblemaData)
      .then(() => {
        store.dispatch(add_toast_action({
          title: "Modifiche salvate",
          message: <>
            Le modifiche alla bozza {values.titolo} sono state correttamente salvate sul server.
          </>,
          time: new Date(),
        }));
      })
      .catch((error: APIError) => handle_drf_form_error(error, actions, setError))
      .finally(() => actions.setSubmitting(false))
  }

  const handleDelete = async (values: FormValues, actions: FormikActions<FormValues>) => {
    delete_bozza(props.pk)
      .then(() => window.location.assign(`/#/drafts/`))
      .catch((error: APIError) => handle_drf_form_error(error, actions, setError))
  }


  const handlePubblica = async (values: FormValues, actions: FormikActions<FormValues>) => {
    const submitting_data = {
      titolo: values.titolo_meme,
      problema: {
        ...values,
        ...values.problema,
        testo: undefined,
        versioni: [{
          testo: values.testo,
        }]
      }
    }
    return post_new_problema(submitting_data)
      .then(ans => delete_bozza(props.pk)
        .then(() => navigate(`/thread/problema/${ans.id}/`))
      )
      .catch((error: APIError) => {
        handle_drf_form_error(
          error, actions, setError,
          msg => {
            // @ts-ignore
            const ret = deserialize_versioned(msg.problema);
            if (ret.soluzioni) {
              if (ret.problema) {
                ret.problema.soluzioni = cloneDeep(ret.soluzioni)
              } else {
                ret.problema = { soluzioni: cloneDeep(ret.soluzioni) }
              }
            }
            return ret
          }
        )
      })
  }

  const handleSubmit = async (values: FormValues, actions: FormikActions<FormValues>) => {
    let cusumano;
    switch (subButton.current) {
      case Bottone.Save: cusumano = handleUpdate(values, actions); break;
      case Bottone.Pubblica: cusumano = handlePubblica(values, actions); break;
      default: sentry_log(
        new Error(
          `ModificaBozza::handleSubmit chiamata con il parametro sbagliato: ${subButton}`));
        return;
    }
    cusumano.finally(() => actions.setSubmitting(false));
  }

  const lint = (
    fieldname: string,
    values: FormValues,
    setFieldValue: FormikActions<FormValues>["setFieldValue"],
    newval: string
  ) => {
    setFieldValue(fieldname, newval)
    if (new Date().getTime() - lastLint.current.getTime() > 1000 * 10 && !!newval && newval != "") {
      lastLint.current = new Date();
      lint_text(newval).then(linted => setLints(old => {
        return { ...old, [fieldname]: linted }
      }))
    }
  }

  if (!old) {
    return <LoadingCircle />
  }
  let initial = cloneDeep(old) as unknown as FormValues;
  initial.soluzioni = [];

  return (
    <div>
      <ErrorBlockGeneric error={error} />
      <Formik
        initialValues={initial} onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, isSubmitting, setFieldValue, handleSubmit, handleReset, errors, touched, setFieldTouched }) => {
          return (
            <>
              <Form className="form needs-validation">
                <h3 className="page-header">{values.titolo}</h3>
                <FormGroupBootstrap
                  name="titolo_meme" type="text"
                  displayName="Titolo"
                  help_text="Attenzione, questo campo viene salvato solo ed esclusivamente se pubblichi il problema. Se salvi le modifiche alla bozza, questo campo verrà perso."
                />
                <FormGroupBootstrap
                  name="titolo" type="text"
                  displayName="Titolo serio"
                />
                <span>Se inserisci una domanda, le modifiche verranno salvate solo se pubblichi il problema e non se lo salvi come bozza.</span>
                <FormGroupBootstrap
                  name="tipologia_risposta" type="select"
                  help_text={
                    "Il tipo di risposta ad un problema influenzerà anche a che tipo di gare può essere assegnato un problema. " +
                    "Risposta numerica è il tipico problema da gara a squadre, risposta a scelta multipla è " +
                    "utile solo per la gara di primo livello, mentre risposta aperta è per i problemi che vanno corretti manualmente," +
                    " come Senigallia o il test di ammissione allo stage."
                  }
                  choices={PROBLEMA_TIPOLOGIA_RISPOSTA}
                />

                <CategoriaSelector name="categoria" help_text={"Le categorie colorate sono state selezionate"} />
                <CategoriaEventoSelector name="eventi_proposto" />
                <hr className="my-3" />
                <CompetenzaSelector name="competenze" displayName="Competenze necessarie" />
                <AllegatoMap
                  allegati={values.allegati}
                  pubblicaAllegato={{ allegatiStr: "allegati", setAllegati: val => setFieldValue("allegati", val) }}
                />
                <MinimalLatexEditor
                  name="testo"
                  attachments={values.allegati.map(id => ({ id, titolo: allegati?.find(attachment => attachment.id === id)?.titolo }))}
                  livePreview={<AnteprimaLaTeX fieldname="testo" mode="live" />}
                  displayName="Testo"
                  value={values["testo"] || ''}
                  touched={!!touched["testo"]}
                  error={errors["testo"]}
                  onChange={(value) => lint("testo", values, setFieldValue, value)}
                  onBlur={() => setFieldTouched("testo", true, true)}
                />
                {values.tipologia_risposta === "num" &&
                  <MultipleSoluzioniForm />
                }
                {values.tipologia_risposta === "mul" &&
                  <RisposteMultipleForm />
                }

                <FormGroupBootstrap
                  name="fonte" type="select" choices={FONTE}
                  help_text="Da dove ti sei ispirato per scrivere questo problema?"
                />
                <FormGroupBootstrap
                  name="fonte_verboso" type="text"
                  help_text="Dettagli extra sulla fonte del problema. Obbligatorio se hai selezionato 'Altro'"
                />
                <div className="corrector-notes">
                  <TextHelper field={textarea} fieldname={"commenti_correttori"} />
                  <div className="row align-items-stretch">
                    <div className="col-12 col-lg-6 d-flex flex-column" style={{ minWidth: 0 }}>
                      <div className="card corrector-notes-editor">
                        <div className="card-body">
                          <FormGroupBootstrap name={"commenti_correttori"} type="textarea" ref={textarea} />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-lg-6 d-flex flex-column mt-3 mt-lg-0" style={{ minWidth: 0 }}>
                      <Anteprima fieldname={"commenti_correttori"} />
                    </div>
                  </div>
                  <small className="form-text text-muted">Commenti che non devono finire nel testo del problema, riservati agli organizzatori e correttori</small>
                </div>
                <MinimalLatexEditor
                  name="soluzione_spiegata"
                  attachments={values.allegati.map(id => ({ id, titolo: allegati?.find(attachment => attachment.id === id)?.titolo }))}
                  livePreview={<AnteprimaLaTeX fieldname="soluzione_spiegata" mode="live" />}
                  displayName="Soluzione"
                  helpText="Come si risolve il problema. Non serve mettere la formula finale, quella va messa quando inserisci una domanda."
                  value={values["soluzione_spiegata"] || ''}
                  touched={!!touched["soluzione_spiegata"]}
                  error={errors["soluzione_spiegata"]}
                  onChange={(value) => lint("soluzione_spiegata", values, setFieldValue, value)}
                  onBlur={() => setFieldTouched("soluzione_spiegata", true, true)}
                />

                <div className="d-flex justify-content-end">
                  <ConfirmedButton
                    type="warning"
                    onSubmit={handleReset}>
                    Reset
                  </ConfirmedButton>
                  <ConfirmedButton
                    type="danger"
                    onSubmit={handleDelete}
                  >
                    Elimina la bozza
                  </ConfirmedButton>
                  <button type="button" disabled={initial == values}
                    onClick={() => { subButton.current = Bottone.Save; handleSubmit() }}
                    className="mx-1 btn btn-belize-hole">Salva le modifiche</button>
                  <button type="button" disabled={isSubmitting}
                    onClick={() => { subButton.current = Bottone.Pubblica; handleSubmit() }}
                    className="mx-1 btn btn-primary">Pubblica come problema</button>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}

export default ModificaBozza;
